import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost } from "../../../services/api";
import Button from "../../common/Button";
import "./AdminFinance.css";

interface FinanceSummary {
  cashflow?: { inflow?: number; outflow?: number; net?: number };
  pnl?: Record<string, { revenue?: number; expense?: number }>;
}

interface FinanceData {
  start_date: string;
  end_date: string;
  summary: FinanceSummary;
}

export default function AdminFinance() {
  const [period, setPeriod] = useState<"day" | "week" | "month" | "half_yearly">("day");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [halfYear, setHalfYear] = useState<"H1" | "H2">("H1");
  const [data, setData] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drill-down state
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [detailsData, setDetailsData] = useState<any[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Manual Transaction State
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [txForm, setTxForm] = useState({
    type: "EXPENSE", // INCOME or EXPENSE
    source: "",
    amount: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [txLoading, setTxLoading] = useState(false);

  const loadDetails = async (source: string) => {
    setSelectedSource(source);
    setDetailsLoading(true);
    setDetailsData([]);
    try {
      let start = data?.start_date;
      let end = data?.end_date;
      const response = await apiGet(`/api/finance/events?source=${source}&start_date=${start}&end_date=${end}`);
      setDetailsData((response as any[]) || []);
    } catch (e) {
      console.error("Failed to load details", e);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setSelectedSource(null);
    setDetailsData([]);
  };

  const loadFinanceData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let start = new Date(selectedDate);
      let end = new Date(selectedDate);

      if (period === "week") {
        end = new Date(start);
        end.setDate(start.getDate() + 6);
      } else if (period === "month") {
        start = new Date(selectedDate.substring(0, 7) + "-01");
        end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      } else if (period === "half_yearly") {
        const year = parseInt(selectedDate.substring(0, 4), 10);
        if (halfYear === "H1") {
          start = new Date(year, 0, 1);
          end = new Date(year, 5, 30);
        } else {
          start = new Date(year, 6, 1);
          end = new Date(year, 11, 31);
        }
      }

      const startStr = start.toISOString().substring(0, 10);
      const endStr = end.toISOString().substring(0, 10);

      const response = await apiGet(
        `/api/finance/events/summary?start_date=${startStr}&end_date=${endStr}`
      ) as FinanceSummary;
      setData({
        start_date: startStr,
        end_date: endStr,
        summary: response,
      });
    } catch (err) {
      console.error("Failed to load finance data", err);
      setError("Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, [period, selectedDate, halfYear]);

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  const handleSaveTransaction = async () => {
    if (!txForm.amount || isNaN(Number(txForm.amount)) || Number(txForm.amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    if (!txForm.source.trim()) {
      alert("Please enter a source/category.");
      return;
    }
    if (!txForm.description.trim()) {
      alert("Please enter a description.");
      return;
    }

    setTxLoading(true);
    try {
      const payload = {
        date: txForm.date,
        source: txForm.source.toUpperCase(),
        description: txForm.description,
        debit: txForm.type === "INCOME" ? parseFloat(txForm.amount) : 0,
        credit: txForm.type === "EXPENSE" ? parseFloat(txForm.amount) : 0,
        ref_id: "MANUAL-" + Date.now(),
        meta: { manual: true }
      };
      await apiPost("/api/finance/events", payload);
      setShowTransactionModal(false);
      setTxForm({ ...txForm, amount: "", description: "" });
      loadFinanceData();
    } catch (e) {
      alert("Failed to save transaction");
      console.error(e);
    } finally {
      setTxLoading(false);
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    return `₹${(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="mod-standalone-window finance-dashboard-modern">
      <div className="mod-window-header finance-window-header">
        <div className="finance-header-row">
          <div>
            <h2 className="finance-title">💰 Financial Analytics</h2>
            <p className="mod-text-muted">Review clinic cashflow, P&L, and manual entries.</p>
          </div>
          <Button type="button" variant="primary" onClick={() => setShowTransactionModal(true)}>+ Add Entry</Button>
        </div>
      </div>

      {/* Controls */}
      <div className="finance-header">
        <div className="header-controls-left">
          <div className="mod-sub-nav period-toggle">
          {(["day", "week", "month", "half_yearly"] as const).map(p => (
            <button key={p} className={`mod-sub-btn ${period === p ? "active" : ""}`} onClick={() => setPeriod(p)}>
              {p.replace("_", " ").toUpperCase()}
            </button>
          ))}
          </div>
        </div>
        <div className="header-controls-right date-picker-wrapper">
          {period === "half_yearly" && (
            <div className="mod-sub-nav half-year-toggle">
              <button
                className={`mod-sub-btn ${halfYear === "H1" ? "active" : ""}`}
                type="button"
                onClick={() => setHalfYear("H1")}
              >
                H1
              </button>
              <button
                className={`mod-sub-btn ${halfYear === "H2" ? "active" : ""}`}
                type="button"
                onClick={() => setHalfYear("H2")}
              >
                H2
              </button>
            </div>
          )}
          <input
            type={period === "half_yearly" ? "number" : period === "month" ? "month" : "date"}
            value={period === "half_yearly" ? selectedDate.substring(0, 4) : period === "month" ? selectedDate.substring(0, 7) : selectedDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              if (period === "half_yearly") {
                setSelectedDate(val + "-01-01");
              } else if (period === "month") {
                setSelectedDate(val + "-01");
              } else {
                setSelectedDate(val);
              }
            }}
            className="date-input"
            min={period === "half_yearly" ? "2020" : undefined}
            max={period === "half_yearly" ? new Date().getFullYear().toString() : undefined}
          />
        </div>
      </div>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {loading && !data && (
        <div className="loading-state">Loading financial data...</div>
      )}

      {data && (
        <>
          {/* Metric Cards */}
          <div className="finance-summary-cards">
            <div className="summary-card summary-card-inflow">
              <div className="summary-label">Total Inflow</div>
              <div className="summary-value summary-value-inflow">₹{data?.summary.cashflow?.inflow?.toLocaleString() || '0'}</div>
            </div>
            <div className="summary-card summary-card-outflow">
              <div className="summary-label">Total Outflow</div>
              <div className="summary-value summary-value-outflow">₹{data?.summary.cashflow?.outflow?.toLocaleString() || '0'}</div>
            </div>
            <div className="summary-card summary-card-net">
              <div className="summary-label">Net Cashflow</div>
              <div className="summary-value summary-value-net">₹{data?.summary.cashflow?.net?.toLocaleString() || '0'}</div>
            </div>
          </div>

          <div className="breakdown-grid">
            <div className="breakdown-panel">
              <h4>📊 Revenue Sources</h4>
              <div className="breakdown-list">
                {Object.entries(data.summary.pnl || {})
                  .filter(([_, v]) => (v.revenue || 0) > 0)
                  .map(([src, v]) => (
                    <div key={src} className="breakdown-item clickable" onClick={() => loadDetails(src)}>
                      <div className="item-row">
                        <span className="item-label">{src}</span>
                        <span className="item-value revenue">+{formatCurrency(v.revenue)}</span>
                      </div>
                    </div>
                  ))}
                {Object.values(data.summary.pnl || {}).every(v => !v.revenue) && <div className="no-data-text">No revenue records</div>}
              </div>
            </div>

            <div className="breakdown-panel">
              <h4>📉 Expense Breakdown</h4>
              <div className="breakdown-list">
                {Object.entries(data.summary.pnl || {})
                  .filter(([_, v]) => (v.expense || 0) > 0)
                  .map(([src, v]) => (
                    <div key={src} className="breakdown-item clickable" onClick={() => loadDetails(src)}>
                      <div className="item-row">
                        <span className="item-label">{src}</span>
                        <span className="item-value expense">-{formatCurrency(v.expense)}</span>
                      </div>
                    </div>
                  ))}
                {Object.values(data.summary.pnl || {}).every(v => !v.expense) && <div className="no-data-text">No expense records</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Details Modal */}
      {selectedSource && (
        <div className="modal-overlay">
          <div className="modal-content finance-details-modal">
            <div className="modal-header">
              <h3>{selectedSource} Details ({data?.start_date} to {data?.end_date})</h3>
              <button className="close-btn" onClick={closeDetails}>&times;</button>
            </div>
            <div className="modal-body">
              {detailsLoading ? (
                <div className="loading-spinner">Loading...</div>
              ) : (
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Ref ID</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailsData.length === 0 ? (
                      <tr><td colSpan={4} className="no-data">No records found.</td></tr>
                    ) : (
                      detailsData.map((row: any) => {
                        const isIncome = row.debit > 0;
                        return (
                          <tr key={row.id}>
                            <td>{new Date(row.event_date).toLocaleDateString()}</td>
                            <td className="mono">{row.ref_id}</td>
                            <td>{row.description}</td>
                            <td className={isIncome ? "amount-in" : "amount-out"}>
                              {isIncome ? "+" : "-"}{formatCurrency(isIncome ? row.debit : row.credit)}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manual Transaction</h3>
              <button className="close-btn" onClick={() => setShowTransactionModal(false)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="form-body">
                <div className="form-group">
                  <label>Type</label>
                  <div className="toggle-group">
                  <button
                    type="button"
                    className={`tx-toggle ${txForm.type === "INCOME" ? "active income" : ""}`}
                    onClick={() => setTxForm({ ...txForm, type: "INCOME" })}
                  >Income</button>
                  <button
                    type="button"
                    className={`tx-toggle ${txForm.type === "EXPENSE" ? "active expense" : ""}`}
                    onClick={() => setTxForm({ ...txForm, type: "EXPENSE" })}
                  >Expense</button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={txForm.date} onChange={e => setTxForm({ ...txForm, date: e.target.value })} />
                </div>

                <div className="form-group">
                  <label>Category / Source</label>
                  <input
                    type="text"
                    placeholder="e.g. RENT, SALARY, SCRAP_SALE"
                    value={txForm.source}
                    onChange={e => setTxForm({ ...txForm, source: e.target.value })}
                    list="source-suggestions"
                  />
                  <datalist id="source-suggestions">
                    <option value="RENT" />
                    <option value="SALARY" />
                    <option value="ELECTRICITY" />
                    <option value="MAINTENANCE" />
                    <option value="SCRAP_SALE" />
                  </datalist>
                </div>

                <div className="form-group">
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={txForm.amount}
                    onChange={e => setTxForm({ ...txForm, amount: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    placeholder="Notes..."
                    value={txForm.description}
                    onChange={e => setTxForm({ ...txForm, description: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button type="button" variant="ghost" onClick={() => setShowTransactionModal(false)}>Cancel</Button>
              <Button type="button" variant="primary" onClick={handleSaveTransaction} isLoading={txLoading}>Save Transaction</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
